/**
 * URL utility functions for handling frontend URLs and navigation
 */

import config from '../config/env.config';

/**
 * Get the frontend base URL
 * @returns {string} Frontend base URL
 */
export const getFrontendUrl = () => {
  return config.FRONTEND_URL;
};

/**
 * Build a full frontend URL with a path
 * @param {string} path - Path to append to frontend URL
 * @returns {string} Full frontend URL
 */
export const buildFrontendUrl = (path = '') => {
  const baseUrl = config.FRONTEND_URL;
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl}${cleanPath}`;
};

/**
 * Get common frontend URLs
 */
export const frontendUrls = {
  home: () => buildFrontendUrl('/'),
  menu: () => buildFrontendUrl('/menu'),
  delivery: () => buildFrontendUrl('/delivery'),
  takeaway: () => buildFrontendUrl('/takeaway'),
  catering: () => buildFrontendUrl('/catering'),
  tableReservations: () => buildFrontendUrl('/table-reservations'),
  tableOrder: (uniqueString) => buildFrontendUrl(`/table-order/${uniqueString}`),
  checkout: () => buildFrontendUrl('/checkout'),
  orderSuccess: () => buildFrontendUrl('/order-success'),
  paymentSuccess: () => buildFrontendUrl('/payment/success'),
  about: () => buildFrontendUrl('/about'),
  contact: () => buildFrontendUrl('/contact'),
  privacy: () => buildFrontendUrl('/privacy'),
  terms: () => buildFrontendUrl('/terms'),
  login: () => buildFrontendUrl('/login'),
  admin: () => buildFrontendUrl('/admin'),
  adminDashboard: () => buildFrontendUrl('/admin/dashboard'),
};

/**
 * Navigate to a frontend URL
 * @param {string} path - Path to navigate to
 * @param {boolean} newTab - Whether to open in new tab
 */
export const navigateToFrontend = (path, newTab = false) => {
  const url = buildFrontendUrl(path);
  if (newTab) {
    window.open(url, '_blank');
  } else {
    window.location.href = url;
  }
};

/**
 * Get API URL for a specific endpoint
 * @param {string} endpoint - API endpoint
 * @returns {string} Full API URL
 */
export const getApiUrl = (endpoint = '') => {
  return config.getApiUrl(endpoint);
};

/**
 * Get static image URL
 * @param {string} imagePath - Image path
 * @returns {string} Full image URL
 */
export const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  if (imagePath.startsWith('http')) return imagePath;
  return `${config.API_URL}/static/images/${imagePath}`;
};

export default {
  getFrontendUrl,
  buildFrontendUrl,
  frontendUrls,
  navigateToFrontend,
  getApiUrl,
  getImageUrl,
};
